import type { ProvedorMapa } from "../models/roteirizacaoModel"
import { COR_SELECIONADO, corTextoSobre } from "./coresEntregador"

export interface MarcadorPedido {
    id: number
    latitude: number
    longitude: number
    cliente: string
    /** Código curto do pedido, exibido como rótulo no ponto do mapa. */
    shortReference: string
    nomeEntregador: string | null
    selecionado: boolean
    /** Cor do marcador conforme o status (ver coresEntregador.ts). */
    cor: string
}

export interface MapaRoteirizacaoBaseProps {
    origem: { lat: number; lng: number } | null
    marcadores: MarcadorPedido[]
    onSelecionarPedido: (id: number) => void
}

export interface MapaRoteirizacaoProps extends MapaRoteirizacaoBaseProps {
    provedor: ProvedorMapa
    tokenMapbox: string
}

/** Centro padrão (Brasil) quando não há origem nem marcadores. */
export const CENTRO_PADRAO = { lat: -14.235, lng: -51.9253 }

/**
 * Iniciais do entregador para exibir junto ao número do pedido no mapa:
 * primeira letra do primeiro nome + primeira letra do último nome. Quando o
 * entregador não tem sobrenome, usa as duas primeiras letras do nome. Sempre
 * em maiúsculas; string vazia quando não há nome.
 */
export function iniciaisEntregador(nome: string | null | undefined): string {
    if (!nome) return ""
    const partes = nome.trim().split(/\s+/).filter(Boolean)
    if (partes.length === 0) return ""
    if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase()
    const primeiro = partes[0][0]
    const ultimo = partes[partes.length - 1][0]
    return (primeiro + ultimo).toUpperCase()
}

/** Texto de dica (hover) do marcador. */
export function dicaMarcador(m: MarcadorPedido): string {
    return `#${m.shortReference} - ${m.cliente}${m.nomeEntregador ? ` · ${m.nomeEntregador}` : ""}`
}

/**
 * Estilos do marcador em formato de "etiqueta" (caixa colorida com o número do
 * pedido + seta apontando o endereço do cliente), compartilhados entre Google
 * Maps (style do React) e Mapbox (aplicados direto no elemento). Valores sempre
 * em string para servirem aos dois. Tamanho 3x o original reduzido em 30% (~2,1x),
 * para a etiqueta ficar legível com o mapa afastado.
 */
export function estilosEtiqueta(m: MarcadorPedido): {
    container: Record<string, string>
    caixa: Record<string, string>
    numero: Record<string, string>
    iniciais: Record<string, string>
    seta: Record<string, string>
} {
    const borda = m.selecionado ? COR_SELECIONADO : "#ffffff"
    return {
        container: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            transform: m.selecionado ? "scale(1.15)" : "none",
            transformOrigin: "bottom center",
        },
        caixa: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: m.cor,
            color: corTextoSobre(m.cor),
            border: `2px solid ${borda}`,
            borderRadius: "6px",
            padding: "5.5px 10.5px",
            boxShadow: m.selecionado
                ? `0 0 0 3px ${COR_SELECIONADO}, 0 2px 4px rgba(0,0,0,0.4)`
                : "0 2px 4px rgba(0,0,0,0.4)",
            whiteSpace: "nowrap",
            lineHeight: "1",
        },
        numero: { fontSize: "25px", fontWeight: "700" },
        iniciais: { fontSize: "17px", fontWeight: "700", marginTop: "3.5px", opacity: "0.9" },
        seta: {
            width: "0",
            height: "0",
            borderLeft: "11px solid transparent",
            borderRight: "11px solid transparent",
            borderTop: `14px solid ${m.selecionado ? COR_SELECIONADO : m.cor}`,
            marginTop: "-2px",
        },
    }
}
